<template>
  <div>
    <custom-callout
      :border-color="'gray'"
      class-main="mt-1 rounded"
      :has-toggle="false"
      :opened="false"
    >
      <div class="">
        <div class="flex-wrap-mobile d-flex flex-row pt-2">
          <div class="d-flex flex-row justify-content-center p-2">
            <div class="dot text-center color-white">
              <i class="fas fa-map-marker-alt" />
            </div>
          </div>
          <div class="d-flex flex-row flex-wrap">
            <div class="p-2" style="min-width: 200px; max-width: 250px;">
              <div class="color-gray-6 font-14">
                Dirección {{ address.addressTypeDescription | empty }}:
              </div>
              <div class="color-black font-16">
                {{ fullAddress(address) }}
              </div>
            </div>
            <div class="p-2" style="min-width: 200px; max-width: 250px;">
              <div class="color-gray-6 font-14">Latitud:</div>
              <div class="color-black font-16">
                {{ address.latitude | empty }}
              </div>
            </div>
            <div class="p-2" style="min-width: 200px; max-width: 250px;">
              <div class="color-gray-6 font-14">Longitud:</div>
              <div class="color-black font-16">
                {{ address.longitude | empty }}
              </div>
            </div>
            <div class="p-2" style="min-width: 200px; max-width: 250px;">
              <div class="color-gray-6 font-14">Información Adicional:</div>
              <div class="color-black font-16">
                {{ address.additionalAnnotations | empty }}
              </div>
            </div>
            <div class="p-2" style="min-width: 200px;" />
            <div class="p-2" style="min-width: 200px;" />
          </div>
          <div class="p-2 d-flex flex-row justify-content-center">
            <drop-down :items="actionItems(address)" />
          </div>
        </div>
      </div>
    </custom-callout>

    <div class="mt-3">
      <div v-if="showEdit" class="bg-secondary-3 rounded p-2">
        <address-form
          v-model="model"
          :address-types="addressTypes"
          class="bg-white"
          :data="data"
          :municipalities="municipalities"
          @cancel="cleanModel"
          @save="save"
        />
      </div>
    </div>
  </div>
</template>

<script>
import CustomCallout from 'custom-callout';
import DropDown from 'dropdown';
import AddressTypes from '@/utils/constants/addressTypes.js';
import AddressForm from './LocationServiceAddress.vue';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import {
  getAddressTypes as _getAddressTypes,
  editLocationAddress as _editLocationAddress,
  deleteLocationAddress as _deleteLocationAddress,
} from '@/services/common/location.api.js';

export default {
  name: 'LocationServiceAddressesRow',
  components: {
    CustomCallout,
    DropDown,
    AddressForm,
  },
  mixins: [alertMixin],
  props: {
    address: {
      type: Object,
      required: true,
      default: () => ({}),
    },
    locationId: {
      type: Number,
      default: null,
    },
    municipalities: {
      type: Array,
      default: () => [],
    },
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      showEdit: false,
      addressTypes: [],
      model: {
        id: null,
        addressTypeCd: null,
        addressTypeDescription: null,
        addressLine1: null,
        addressLine2: null,
        addressLine3: null,
        municipalityId: null,
        municipalityDescription: null,
        zipCode: null,
        latitude: null,
        longitude: null,
        additionalAnnotations: null,
      },
    };
  },
  async mounted() {
    await Promise.all([this.getAddressTypes()]);
  },
  computed: {
    fullAddress() {
      return (item) => {
        const address = `${item.addressLine1 || ''} ${item.addressLine2 ||
          ''} ${item.addressLine3 || ''} ${item.municipalityDescription ||
          ''}`.trim();
        return `${address}, Puerto Rico ${item.zipCode}`;
      };
    },
    isPhysicalAddress() {
      return (item) => {
        return item.addressTypeCd === AddressTypes.Physical;
      };
    },
    actionItems() {
      return (item) => {
        return [
          {
            label: 'Editar',
            icon: 'fas fa-edit color-green',
            border: this.isPhysicalAddress(item) ? '' : 'bottom',
            click: () => this.showAddressForm(item),
          },
          {
            label: 'Eliminar',
            icon: 'fas fa-times-circle color-red',
            visible: !this.isPhysicalAddress(item),
            click: () => this.deleteLocationAddress(item),
          },
        ];
      };
    },
  },
  methods: {
    showAddressForm(item) {
      this.showEdit = true;
      if (item) this.model = item;
    },
    async getAddressTypes() {
      const { data } = await _getAddressTypes();
      this.addressTypes = data;
    },
    async save() {
      await this.editLocationAddress();
      this.cleanModel();
    },
    async editLocationAddress() {
      const payload = { ...this.model, locationId: this.locationId };
      const { data } = await _editLocationAddress(payload.id, payload).catch(
        (error) => {
          const message = error?.response?.data?.message ?? error.message;
          this.ShowToast('Alerta', `${message}`, 'error');
          return { data: false };
        }
      );

      if (data) {
        this.ShowToast(
          `Confirmación`,
          'La dirección ha sido actualizada exitosamente.',
          'success'
        );
        this.$emit('on-submit');
      }
    },
    async deleteLocationAddress({ id, addressTypeCd, addressTypeDescription }) {
      const isPhysicalAddress = addressTypeCd === AddressTypes.Physical;
      if (isPhysicalAddress) {
        await this.ShowToast(
          'Confirmación',
          'La dirección Física no puede ser eliminada.',
          'error'
        );
        return;
      }
      const answer = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea eliminar la dirección ${addressTypeDescription}?`,
        'Sí',
        'No'
      );
      if (!answer) return;

      const { data } = await _deleteLocationAddress(id).catch((error) => {
        const message = error?.response?.data?.message ?? error.message;
        this.ShowToast('Alerta', `${message}.`, 'error');
        return { data: false };
      });

      if (data) {
        this.ShowToast(
          `Confirmación`,
          'La dirección ha sido eliminada exitosamente.',
          'success'
        );

        this.$emit('on-submit');
      }
    },
    cleanModel() {
      this.showEdit = false;
      this.model = {
        id: null,
        addressTypeCd: null,
        addressTypeDescription: null,
        addressLine1: null,
        addressLine2: null,
        addressLine3: null,
        municipalityId: null,
        municipalityDescription: null,
        zipCode: null,
        latitude: null,
        longitude: null,
        additionalAnnotations: null,
      };
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/shared/scss/config/colors.scss';
.dot {
  background-color: map-get($colors, 'orange');
  box-shadow: 0px 3px 6px #00000029;
  margin: 2px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border-style: solid;
  border-width: 3px;
  border-color: map-get($colors, 'white');
  display: inline-block;
}

.custom-col-sm-5 {
  flex: 0 0 41.66666667%;
  max-width: 37.9%;
}

@media screen and (max-width: 768px) {
  .mobile-only {
    display: block;
  }
  .mt-sm-n5 {
    margin-top: 0 !important;
  }
  .ml-sm-n4 {
    margin-left: 0 !important;
  }
}
</style>
